import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { requestResetCode, confirmResetPassword } from '../services/authRecovery';
import styles from './LoginForm.module.css'; // Reusing styles

const ForgotPassword: React.FC = () => {
    const [stage, setStage] = useState<'request' | 'confirm'>('request');
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const navigate = useNavigate();

    const handleRequest = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        try {
            await requestResetCode(email.trim());
            setStage('confirm');
            setSuccess('Reset code sent to your email.');
        } catch (err: any) {
            setError(err.message || 'Failed to send reset code.');
        }
    };

    const handleConfirm = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        try {
            // The recovery service strips formatting from the code before it is
            // hashed, so a pasted code with spaces still verifies.
            await confirmResetPassword(email.trim(), code, newPassword);
            setSuccess('Password reset successful! Redirecting to login...');
            setTimeout(() => navigate('/login'), 2000);
        } catch (err: any) {
            setError(err.message || 'Failed to reset password.');
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.formWrapper}>
                <div className={styles.logoContainer}>
                    <img src="/Landlord-Pro-Logo.png" alt="Landlord Pro" className={styles.logo} />
                </div>
                <div className={styles.card}>
                    <h2 style={{ textAlign: 'center', marginBottom: '20px', color: '#1f2937' }}>
                        {stage === 'request' ? 'Reset Password' : 'Set New Password'}
                    </h2>
                    <div className={styles.formSection}>
                        {stage === 'request' ? (
                            <form onSubmit={handleRequest}>
                                <label className={styles.label}>Enter your email address</label>
                                <input
                                    className={styles.input}
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Email"
                                    required
                                />

                                {error && <p className={styles.error}>{error}</p>}
                                {success && <p className={styles.success} style={{ color: 'green', fontSize: '14px', marginBottom: '10px' }}>{success}</p>}

                                <button type="submit" className={styles.loginButton}>
                                    <span>Send Reset Code</span>
                                </button>
                            </form>
                        ) : (
                            <form onSubmit={handleConfirm}>
                                <label className={styles.label}>Reset Code (from email)</label>
                                <input
                                    className={styles.input}
                                    type="text"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    placeholder="123456"
                                    required
                                />

                                <label className={styles.label}>New Password</label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        className={styles.input}
                                        type={showPassword ? "text" : "password"}
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="New Password"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        style={{
                                            position: 'absolute',
                                            right: '10px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            color: '#666'
                                        }}
                                    >
                                        {showPassword ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '20px', height: '20px' }}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                                            </svg>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '20px', height: '20px' }}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                        )}
                                    </button>
                                </div>

                                {error && <p className={styles.error}>{error}</p>}
                                {success && <p className={styles.success} style={{ color: 'green', fontSize: '14px', marginBottom: '10px' }}>{success}</p>}

                                <button type="submit" className={styles.loginButton}>
                                    <span>Reset Password</span>
                                </button>
                            </form>
                        )}
                    </div>

                    <div className={styles.bottomSection}>
                        <div className={styles.bottomGrid}>
                            <div className={styles.leftAlign}>
                                <button className={styles.backButton} onClick={() => navigate('/login')}>
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                    </svg>
                                    <span>Back to Login</span>
                                </button>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;
